import { Head, Link, router } from "@inertiajs/react"
import { AlertTriangle, Clock, FileText, Plus, Receipt } from "lucide-react"
import type { ComponentType } from "react"

import { Button } from "@/components/ui/button"
import { CostChart, type CostPoint } from "@/components/steel/cost-chart"
import { DataTable, Primary, Td, Th, Thead, Tr } from "@/components/steel/table"
import { Kpi, KpiStrip, Meter, PageHeader, Section, StatusBadge } from "@/components/steel"
import { stageLabel, stageOpacity } from "@/lib/projects"
import { formatCurrency, shortMoney } from "@/lib/utils"

type ProjectRow = {
  id: string
  name: string
  meta: string
  stage: string
  status: string
  budget: number
  spend: number
  revenue: number
  pctUsed: number
  teamSize: number
}

type BidRow = {
  id: string
  name: string
  meta: string
  client: string | null
  status: string
  price: number
  sqft: number
  perSqft: number
}

type AttentionItem = {
  kind: string
  title: string
  sub: string
  value: string
  href: string
}

type Props = {
  kpis: {
    contractValue: number
    costToDate: number
    budget: number
    projectedProfit: number
    profitLow: number
    profitHigh: number
    unbilled: number
    openBidCount: number
    openBidValue: number
    activeProjects: number
  }
  burndown: { points: CostPoint[]; budget: number }
  attention: AttentionItem[]
  projects: ProjectRow[]
  bids: BidRow[]
}

const ATTENTION_ICON: Record<string, ComponentType<{ className?: string }>> = {
  alert: AlertTriangle,
  bid: FileText,
  invoice: Receipt,
  time: Clock,
}

export default function Dashboard({ kpis, burndown, attention, projects, bids }: Props) {
  const budgetUsed = kpis.budget > 0 ? Math.round((kpis.costToDate / kpis.budget) * 100) : 0

  return (
    <>
      <Head title="Overview" />

      <PageHeader
        title="Overview"
        meta={new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
        actions={
          <Button size="sm" onClick={() => router.visit("/bids/new")}>
            <Plus />
            New bid
          </Button>
        }
      />

      <KpiStrip>
        <Kpi
          label="Contract value"
          value={shortMoney(kpis.contractValue)}
          hint={`Across ${kpis.activeProjects} active ${kpis.activeProjects === 1 ? "project" : "projects"}`}
          accent
        />
        <Kpi
          label="Cost to date"
          value={shortMoney(kpis.costToDate)}
          hint={`${budgetUsed}% of ${shortMoney(kpis.budget)} budget`}
        />
        <Kpi
          label="Projected profit"
          value={shortMoney(kpis.projectedProfit)}
          hint={`${shortMoney(kpis.profitLow)} – ${shortMoney(kpis.profitHigh)} range`}
        />
        <Kpi label="Unbilled" value={shortMoney(kpis.unbilled)} hint="Labor and expenses accrued" />
        <Kpi
          label="Open bids"
          value={kpis.openBidCount}
          hint={`${shortMoney(kpis.openBidValue)} in the pipeline`}
        />
      </KpiStrip>

      <div className="grid border-b border-border lg:grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)]">
        <div className="border-border px-6 py-4 lg:border-r">
          <div className="mb-1">
            <h2 className="text-title font-semibold">Cost against budget</h2>
            <p className="mt-0.5 text-meta text-muted-foreground">All active projects, cumulative</p>
          </div>
          <CostChart points={burndown.points} budget={burndown.budget} height={212} bands={4} />
        </div>

        <div className="px-6 py-4">
          <h2 className="text-title font-semibold">Needs attention</h2>
          <p className="mb-3 mt-0.5 text-meta text-muted-foreground">
            {attention.length} {attention.length === 1 ? "item" : "items"} across projects and billing
          </p>
          {attention.length === 0 ? (
            <p className="border-t border-border py-6 text-label text-muted-foreground">
              Nothing needs your attention.
            </p>
          ) : (
            attention.map((item) => {
              const Icon = ATTENTION_ICON[item.kind] ?? FileText
              return (
                <Link
                  key={`${item.kind}-${item.href}-${item.title}`}
                  href={item.href}
                  className="flex w-full items-start gap-2.5 border-t border-border px-1 py-2.5 text-left hover:bg-secondary"
                >
                  <Icon
                    className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${item.kind === "alert" ? "text-warn" : "text-foreground"}`}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block text-body font-medium">{item.title}</span>
                    <span className="mt-px block text-meta text-muted-foreground">{item.sub}</span>
                  </span>
                  <span
                    className={`num text-label font-medium ${item.kind === "alert" ? "text-warn" : "text-foreground"}`}
                  >
                    {item.value}
                  </span>
                </Link>
              )
            })
          )}
        </div>
      </div>

      <Section
        title="Projects"
        action={<Link href="/projects" className="text-label text-muted-foreground hover:text-foreground">View all</Link>}
        className="pb-0"
      >
        <DataTable>
          <Thead>
            <Th>Project</Th>
            <Th>Stage</Th>
            <Th className="w-[180px]">Budget used</Th>
            <Th align="right">Spend</Th>
            <Th align="right">Contract</Th>
            <Th align="right">Status</Th>
          </Thead>
          <tbody>
            {projects.map((p) => (
              <Tr key={p.id} onClick={() => router.visit(`/projects/${p.id}`)}>
                <Td>
                  <Primary title={p.name} sub={p.meta} />
                </Td>
                <Td>
                  <span className="inline-flex items-center gap-1.5">
                    <span
                      className="h-[5px] w-[5px] rounded-full bg-foreground"
                      style={{ opacity: stageOpacity(p.stage) }}
                    />
                    {stageLabel(p.stage)}
                  </span>
                </Td>
                <Td>
                  <Meter pct={p.pctUsed} label={`${Math.round(p.pctUsed)}%`} />
                </Td>
                <Td align="right" num>
                  {formatCurrency(p.spend)}
                </Td>
                <Td align="right" num accent>
                  {formatCurrency(p.revenue)}
                </Td>
                <Td align="right">
                  <StatusBadge status={p.status} />
                </Td>
              </Tr>
            ))}
          </tbody>
        </DataTable>
      </Section>

      <Section
        title="Open bids"
        action={<Link href="/bids" className="text-label text-muted-foreground hover:text-foreground">View all</Link>}
        className="pb-7"
      >
        <DataTable>
          <Thead>
            <Th>Bid</Th>
            <Th>Client</Th>
            <Th align="right">$/sq ft</Th>
            <Th align="right">Estimate</Th>
            <Th align="right">Status</Th>
          </Thead>
          <tbody>
            {bids.map((b) => (
              <Tr key={b.id} onClick={() => router.visit(`/bids/${b.id}/edit`)}>
                <Td>
                  <Primary title={b.name} sub={b.meta} />
                </Td>
                <Td muted>{b.client}</Td>
                <Td align="right" num muted>
                  ${b.perSqft.toFixed(2)}
                </Td>
                <Td align="right" num accent>
                  {formatCurrency(b.price)}
                </Td>
                <Td align="right">
                  <StatusBadge status={b.status} />
                </Td>
              </Tr>
            ))}
          </tbody>
        </DataTable>
      </Section>
    </>
  )
}
